import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { Quote, ArrowUpRight } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { Breadcrumbs } from "@/components/Breadcrumbs";

export const Route = createFileRoute("/testimonials")({
  head: () => buildSeo({
    title: "Client Testimonials — Reviews of Freelancer Tamal SEO Services",
    description: "Client review verification status and public profile links for Freelancer Tamal. Unsupported quotations have been withdrawn.",
    path: "/testimonials",
    ogEyebrow: "Testimonials",
    keywords: "Freelancer Tamal reviews, SEO consultant testimonials, SEO expert Bangladesh reviews",
  }),
  component: Testimonials,
});

const QUOTES: { name: string; role: string; body: string }[] = [];

function Testimonials() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Testimonials", path: "/testimonials" }]} />
      <Section headingLevel={1} eyebrow="Testimonials" title={<>Client reviews & <span className="text-gradient">verification.</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          Previously published quotations have been withdrawn because their original wording and permission are not verified.
          No verified client quotations are currently published here.
        </p>
      </Section>

      <div className="container-x grid md:grid-cols-2 lg:grid-cols-3 gap-6 pb-24">
        {QUOTES.map((q, i) => (
          <Reveal key={q.name} delay={(i % 3) * 0.08}>
            <figure className="rounded-3xl border border-border bg-card p-8 h-full hover:border-primary/40 transition">
              <Quote className="h-6 w-6 text-primary" />
              <blockquote className="mt-4 text-base leading-relaxed text-foreground/90">
                "{q.body}"
              </blockquote>
              <figcaption className="mt-6 border-t border-border pt-4">
                <div className="font-display text-lg">{q.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{q.role}</div>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>

      <Section eyebrow="Want in?" title="Let's add yours next.">
        <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium">
          Start a project <ArrowUpRight size={16} />
        </Link>
      </Section>
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Person",
        "@id": `${SITE.url}/#person`,
        name: SITE.fullName,
        url: `${SITE.url}/about`,
        jobTitle: "SEO, AEO & GEO Consultant",
        review: QUOTES.map((q) => ({
          "@type": "Review",
          author: { "@type": "Person", name: q.name },
          reviewBody: q.body,
          reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
          itemReviewed: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName },
        })),
      }} />
      <JsonLd data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Testimonials", path: "/testimonials" }])} />
    </>
  );
}
